import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  Target, Upload, Loader2, CheckCircle2, XCircle, FileSpreadsheet, FileJson,
  BarChart3, Eye, RotateCcw, TrendingUp, TrendingDown, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BACKEND_URL,
  createScorerBatch,
  exportScorerBatch,
  getScorerBatch,
  getScorerPriceCheck,
  listScorerBatches,
  showFriendlyApiError,
} from '../lib/api';

const SYMBOLS = ['BTCUSD', 'ETHUSD', 'SOLUSD', 'XRPUSD', 'XAUUSD', 'SPX500', 'TSLA', 'AAPL', 'EURUSD', 'DXY'];
const SCORER_UI_KEY = 'wavekit.scorer.ui';

const readStoredJson = (key) => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeStoredJson = (key, value) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Ignore storage quota/private mode issues and keep the page usable.
  }
};


export default function Scorer() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedBatch, setSelectedBatch] = useState(() => readStoredJson(SCORER_UI_KEY)?.selectedBatch || null);
  const [batchDetail, setBatchDetail] = useState(null);
  const [selectedResult, setSelectedResult] = useState(null);
  const [resultView, setResultView] = useState(() => readStoredJson(SCORER_UI_KEY)?.resultView || 'overlay'); // 'overlay', 'chart', 'original'

  // Form
  const [batchName, setBatchName] = useState('');
  const [symbol, setSymbol] = useState(() => readStoredJson(SCORER_UI_KEY)?.symbol || 'BTCUSD');
  const [priceAvailable, setPriceAvailable] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const loadBatches = useCallback(async () => {
    try {
      setLoading(true);
      const data = await listScorerBatches();
      setBatches(data);
      if (!selectedBatch && data?.length) {
        setSelectedBatch(data[0].id);
      }
    } catch (e) {
      console.error(e);
      showFriendlyApiError(e, 'load scorer batches', { id: 'scorer-batches' });
    } finally {
      setLoading(false);
    }
  }, [selectedBatch]);

  useEffect(() => { loadBatches(); }, [loadBatches]);

  // Check price availability when symbol changes
  useEffect(() => {
    if (symbol) {
      getScorerPriceCheck(symbol)
        .then(d => setPriceAvailable(d))
        .catch(() => setPriceAvailable(null));
    }
  }, [symbol]);

  const loadBatchDetail = useCallback(async (batchId) => {
    try {
      const data = await getScorerBatch(batchId);
      const storedUi = readStoredJson(SCORER_UI_KEY) || {};
      const desiredResultId = selectedResult?.id || storedUi.selectedResultId;
      const matchedResult = data.results?.find((item) => item.id === desiredResultId) || data.results?.[0] || null;
      setBatchDetail(data);
      setSelectedBatch(batchId);
      setSelectedResult(matchedResult);
      if (data.completed_items < data.total_items && data.status !== 'failed') {
        if (pollRef.current) clearTimeout(pollRef.current);
        pollRef.current = setTimeout(() => loadBatchDetail(batchId), 2500);
      }
    } catch (e) {
      console.error(e);
      showFriendlyApiError(e, 'load that scorer batch', { id: 'scorer-batch-detail' });
    }
  }, [selectedResult]);

  useEffect(() => () => { if (pollRef.current) clearTimeout(pollRef.current); }, []);

  useEffect(() => {
    if (!selectedBatch) return;
    if (batchDetail?.id === selectedBatch) return;
    loadBatchDetail(selectedBatch);
  }, [batchDetail?.id, loadBatchDetail, selectedBatch]);

  useEffect(() => {
    writeStoredJson(SCORER_UI_KEY, {
      selectedBatch,
      selectedResultId: selectedResult?.id || null,
      resultView,
      symbol,
    });
  }, [resultView, selectedBatch, selectedResult?.id, symbol]);

  const handleSubmit = async () => {
    if (!batchName.trim() || selectedFiles.length === 0) {
      toast.error('Enter a name and select files');
      return;
    }
    setUploading(true);
    try {
      const result = await createScorerBatch(batchName.trim(), 'prediction_accuracy', symbol, selectedFiles);
      toast.success(`Scoring ${result.total_items} images against ${symbol} historical data`);
      setBatchName('');
      setSelectedFiles([]);
      setSelectedResult(null);
      loadBatches();
      loadBatchDetail(result.batch_id);
    } catch (e) { showFriendlyApiError(e, 'create that scorer batch', { id: 'scorer-create' }); }
    finally { setUploading(false); }
  };

  const handleExport = async (batchId, format) => {
    try {
      const blob = await exportScorerBatch(batchId, format);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `scorer_${batchId}.${format}`;
      a.click();
    } catch (e) { showFriendlyApiError(e, 'export that scorer batch', { id: 'scorer-export' }); }
  };

  const avgScore = (results, field) => {
    if (!results?.length) return 0;
    return (results.reduce((a, r) => a + (r[field] || 0), 0) / results.length).toFixed(1);
  };

  const ScoreBadge = ({ score }) => {
    const color = score >= 70 ? 'text-[#00f5d4]' : score >= 40 ? 'text-[#f5b400]' : 'text-[#ff3b30]';
    const bg = score >= 70 ? 'bg-[#00f5d4]/10' : score >= 40 ? 'bg-[#f5b400]/10' : 'bg-[#ff3b30]/10';
    return <span className={`font-mono font-bold ${color} ${bg} px-2 py-0.5 rounded-full text-xs`}>{score?.toFixed?.(1) || score}</span>;
  };

  return (
    <div className="animate-fade-in" data-testid="scorer-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-white">Prediction Scorer</h1>
          <p className="text-sm text-[#a1a1aa] mt-1">Score old wave predictions against actual historical price outcomes</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Left: Upload + Batch List */}
        <div className="lg:col-span-3 space-y-3">
          {/* New Batch */}
          <Card className="bg-[#14161a] border-white/5">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-white flex items-center gap-2">
                <Target className="w-4 h-4 text-[#f5b400]" /> New Score Batch
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Input data-testid="batch-name-input" value={batchName} onChange={e => setBatchName(e.target.value)}
                placeholder="Batch name..." className="bg-[#0e0f12] border-white/10 focus:border-[#f5b400] rounded-sm text-sm" />

              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-[#a1a1aa] mb-1 block">Symbol (for historical comparison)</label>
                <Select value={symbol} onValueChange={setSymbol}>
                  <SelectTrigger className="bg-[#0e0f12] border-white/10 rounded-sm text-sm font-mono" data-testid="scorer-symbol">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#14161a] border-white/10">
                    {SYMBOLS.map(s => <SelectItem key={s} value={s} className="font-mono text-sm">{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                {priceAvailable && (
                  <div className="mt-1.5 flex items-center gap-1.5">
                    {priceAvailable.available ? (
                      <>
                        <CheckCircle2 className="w-3 h-3 text-[#00f5d4]" />
                        <span className="text-[10px] text-[#00f5d4]">{priceAvailable.data_points} data points via {priceAvailable.source}</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="w-3 h-3 text-[#ff3b30]" />
                        <span className="text-[10px] text-[#ff3b30]">No price data — upload outcome screenshot</span>
                      </>
                    )}
                  </div>
                )}
              </div>

              <div className="wk-upload-zone flex flex-col items-center justify-center py-5 gap-1.5"
                onClick={() => fileRef.current?.click()} data-testid="scorer-upload-zone">
                <Upload className="w-5 h-5 text-[#a1a1aa]/50" />
                <p className="text-[11px] text-[#a1a1aa]">
                  {selectedFiles.length > 0 ? `${selectedFiles.length} files` : 'Upload old prediction charts'}
                </p>
                <input ref={fileRef} type="file" multiple accept="image/png,image/jpeg,image/webp" className="hidden"
                  onChange={e => setSelectedFiles(Array.from(e.target.files || []))} data-testid="scorer-file-input" />
              </div>

              <Button onClick={handleSubmit} disabled={uploading || !batchName.trim() || selectedFiles.length === 0}
                className="w-full bg-[#f5b400] text-black hover:bg-[#d9a000] rounded-sm active:scale-95 transition-transform" data-testid="submit-batch-btn">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Target className="w-4 h-4 mr-2" />}
                Score {selectedFiles.length} Image{selectedFiles.length !== 1 ? 's' : ''}
              </Button>
            </CardContent>
          </Card>

          {/* Batch List */}
          <Card className="bg-[#14161a] border-white/5">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-white">Batches</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[250px]">
                <div className="divide-y divide-white/5" data-testid="batches-list">
                  {batches.map((b, idx) => (
                    <div key={b.id} data-testid={`batch-${idx}`}
                      onClick={() => { loadBatchDetail(b.id); setSelectedResult(null); }}
                      className={`p-3 cursor-pointer transition-colors ${
                        selectedBatch === b.id ? 'bg-[#f5b400]/5 border-l-2 border-[#f5b400]' : 'hover:bg-white/5'
                      }`}>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-white font-medium truncate">{b.name}</span>
                        <span className="text-[10px] font-mono text-[#a1a1aa]">{b.completed_items}/{b.total_items}</span>
                      </div>
                      {b.symbol && <span className="text-[10px] font-mono text-[#f5b400]">{b.symbol}</span>}
                      <Progress value={(b.completed_items / Math.max(1, b.total_items)) * 100} className="h-1 mt-1.5" />
                    </div>
                  ))}
                  {batches.length === 0 && <div className="p-6 text-center text-xs text-[#a1a1aa]">No batches yet</div>}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Middle: Results + Visuals */}
        <div className="lg:col-span-9">
          {batchDetail ? (
            <div className="space-y-3">
              {/* Summary Stats */}
              <Card className="bg-[#14161a] border-white/5">
                <CardContent className="pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="text-base font-semibold text-white">{batchDetail.name}</h3>
                      <span className="text-xs font-mono text-[#f5b400]">{batchDetail.symbol}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="ghost" size="sm" className="text-[#a1a1aa] hover:text-white rounded-sm text-xs"
                        onClick={() => handleExport(batchDetail.id, 'csv')} data-testid="export-scorer-csv">
                        <FileSpreadsheet className="w-3 h-3 mr-1" /> CSV
                      </Button>
                      <Button variant="ghost" size="sm" className="text-[#a1a1aa] hover:text-white rounded-sm text-xs"
                        onClick={() => handleExport(batchDetail.id, 'json')} data-testid="export-scorer-json">
                        <FileJson className="w-3 h-3 mr-1" /> JSON
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    <div className="text-center p-3 rounded-sm bg-white/[0.02]">
                      <p className="text-2xl font-mono font-bold text-[#f5b400]">{avgScore(batchDetail.results, 'prediction_score')}</p>
                      <p className="text-[10px] uppercase tracking-wider text-[#a1a1aa] mt-1">Prediction Score</p>
                    </div>
                    <div className="text-center p-3 rounded-sm bg-white/[0.02]">
                      <p className="text-2xl font-mono font-bold text-[#00f5d4]">{avgScore(batchDetail.results, 'direction_score')}</p>
                      <p className="text-[10px] uppercase tracking-wider text-[#a1a1aa] mt-1">Direction</p>
                    </div>
                    <div className="text-center p-3 rounded-sm bg-white/[0.02]">
                      <p className="text-2xl font-mono font-bold text-white">{avgScore(batchDetail.results, 'target_score')}</p>
                      <p className="text-[10px] uppercase tracking-wider text-[#a1a1aa] mt-1">Target Hit</p>
                    </div>
                    <div className="text-center p-3 rounded-sm bg-white/[0.02]">
                      <p className="text-2xl font-mono font-bold text-white">{batchDetail.completed_items}/{batchDetail.total_items}</p>
                      <p className="text-[10px] uppercase tracking-wider text-[#a1a1aa] mt-1">Processed</p>
                    </div>
                  </div>
                  <Progress value={(batchDetail.completed_items / Math.max(1, batchDetail.total_items)) * 100} className="h-1.5 mt-3" />
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
                {/* Results list */}
                <div className="lg:col-span-2">
                  <Card className="bg-[#14161a] border-white/5">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-white">Items</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <ScrollArea className="h-[450px]">
                        <div className="divide-y divide-white/5" data-testid="scorer-results-list">
                          {batchDetail.results?.map((r, i) => (
                            <div key={r.id || i} data-testid={`scorer-item-${i}`}
                              onClick={() => setSelectedResult(r)}
                              className={`p-3 cursor-pointer transition-colors ${
                                selectedResult?.id === r.id ? 'bg-[#f5b400]/5 border-l-2 border-[#f5b400]' : 'hover:bg-white/5'
                              }`}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs font-mono text-white">#{r.item_index + 1}</span>
                                  {r.direction_correct === true && <TrendingUp className="w-3 h-3 text-[#00f5d4]" />}
                                  {r.direction_correct === false && <TrendingDown className="w-3 h-3 text-[#ff3b30]" />}
                                </div>
                                <ScoreBadge score={r.prediction_score} />
                              </div>
                              <div className="flex items-center gap-3 mt-1 text-[10px] text-[#a1a1aa]">
                                <span>Dir: {r.direction_score}</span>
                                <span>Tgt: {r.target_score?.toFixed?.(1)}</span>
                                <span>Inv: {r.invalidation_score}</span>
                              </div>
                              {r.target_scores?.length > 0 && (
                                <div className="flex gap-1 mt-1">
                                  {r.target_scores.map((t, j) => (
                                    <span key={j} className={`w-2 h-2 rounded-full ${t.reached ? 'bg-[#00f5d4]' : 'bg-[#ff3b30]'}`}
                                      title={`Target ${j + 1}: ${t.reached ? 'HIT' : 'MISS'} (${t.score}/100)`} />
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    </CardContent>
                  </Card>
                </div>

                {/* Visual Detail */}
                <div className="lg:col-span-3 space-y-3">
                  {selectedResult ? (
                    <>
                      {/* View toggle */}
                      <div className="flex items-center gap-1 bg-[#14161a] rounded-sm p-1 border border-white/5">
                        {[
                          { id: 'overlay', label: 'Score Overlay', icon: Eye },
                          { id: 'chart', label: 'Score Chart', icon: BarChart3 },
                          { id: 'original', label: 'Original', icon: RotateCcw },
                        ].map(v => (
                          <button key={v.id} onClick={() => setResultView(v.id)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-medium transition-colors ${
                              resultView === v.id ? 'bg-[#f5b400]/10 text-[#f5b400]' : 'text-[#a1a1aa] hover:text-white'
                            }`} data-testid={`scorer-view-${v.id}`}>
                            <v.icon className="w-3 h-3" /> {v.label}
                          </button>
                        ))}
                      </div>

                      {/* Image */}
                      <Card className="bg-[#0e0f12] border-white/5 overflow-hidden">
                        <CardContent className="p-0">
                          {resultView === 'overlay' && selectedResult.scoring_overlay_path ? (
                            <img src={`${BACKEND_URL}/api/scorer/items/${selectedResult.id}/overlay`}
                              alt="Score Overlay" className="w-full h-auto" data-testid="scorer-overlay-img"
                              onError={e => { e.target.alt = 'Overlay not available'; }} />
                          ) : resultView === 'chart' && selectedResult.scoring_chart_path ? (
                            <img src={`${BACKEND_URL}/api/scorer/items/${selectedResult.id}/chart`}
                              alt="Score Chart" className="w-full h-auto" data-testid="scorer-chart-img"
                              onError={e => { e.target.alt = 'Chart not available'; }} />
                          ) : (
                            <div className="flex items-center justify-center h-[300px] text-[#a1a1aa] text-sm">
                              {resultView === 'original' ? 'Original image stored on server' : 'Visual not yet generated (processing may still be in progress)'}
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      {/* Score Breakdown */}
                      <Card className="bg-[#14161a] border-white/5">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm font-medium text-white">Score Breakdown</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
                            <div className="text-center p-2 rounded-sm bg-white/[0.02]">
                              <p className="text-lg font-mono font-bold">{selectedResult.direction_correct ? (
                                <span className="text-[#00f5d4]">CORRECT</span>
                              ) : selectedResult.direction_correct === false ? (
                                <span className="text-[#ff3b30]">WRONG</span>
                              ) : <span className="text-[#a1a1aa]">N/A</span>}</p>
                              <p className="text-[10px] text-[#a1a1aa] uppercase">Direction</p>
                            </div>
                            <div className="text-center p-2 rounded-sm bg-white/[0.02]">
                              <p className="text-lg font-mono font-bold text-white">{selectedResult.actual_high?.toFixed(1) || 'N/A'}</p>
                              <p className="text-[10px] text-[#a1a1aa] uppercase">Actual High</p>
                            </div>
                            <div className="text-center p-2 rounded-sm bg-white/[0.02]">
                              <p className="text-lg font-mono font-bold text-white">{selectedResult.actual_low?.toFixed(1) || 'N/A'}</p>
                              <p className="text-[10px] text-[#a1a1aa] uppercase">Actual Low</p>
                            </div>
                            <div className="text-center p-2 rounded-sm bg-white/[0.02]">
                              <p className="text-lg font-mono font-bold text-white">{selectedResult.actual_historical_end_price?.toFixed?.(1) || 'N/A'}</p>
                              <p className="text-[10px] text-[#a1a1aa] uppercase">Reality Close</p>
                            </div>
                          </div>
                          <div className="rounded-sm border border-white/5 bg-white/[0.02] px-3 py-2 mb-3">
                            <div className="flex flex-wrap items-center justify-between gap-3 text-[11px] font-mono">
                              <span className="text-[#a1a1aa]">Delta of Reality</span>
                              <span className="text-white">Predicted: {String(selectedResult.predicted_direction || 'unknown').toUpperCase()}</span>
                              <span className="text-white">Actual: {String(selectedResult.actual_direction || 'unknown').toUpperCase()}</span>
                              <span className={selectedResult.direction_correct ? 'text-[#00f5d4]' : selectedResult.direction_correct === false ? 'text-[#ff3b30]' : 'text-[#a1a1aa]'}>
                                {selectedResult.direction_correct === null || selectedResult.direction_correct === undefined ? 'ABSOLUTE CHECK N/A' : selectedResult.direction_correct ? 'ABSOLUTE CHECK PASS' : 'ABSOLUTE CHECK FAIL'}
                              </span>
                            </div>
                          </div>

                          {/* Target details */}
                          {selectedResult.target_scores?.length > 0 && (
                            <div className="space-y-1.5">
                              <p className="text-[10px] uppercase tracking-wider font-bold text-[#a1a1aa]">Target Hits</p>
                              {selectedResult.target_scores.map((t, j) => (
                                <div key={j} className="flex items-center justify-between py-1 px-2 rounded-sm bg-white/[0.02]">
                                  <div className="flex items-center gap-2">
                                    {t.reached ? <CheckCircle2 className="w-3 h-3 text-[#00f5d4]" /> : <XCircle className="w-3 h-3 text-[#ff3b30]" />}
                                    <span className="text-xs text-white">{t.label}</span>
                                    <span className="font-mono text-xs text-[#a1a1aa]">{t.target_price?.toFixed(1)}</span>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="text-[10px] text-[#a1a1aa]">{t.distance_pct?.toFixed(1)}% away</span>
                                    <ScoreBadge score={t.score} />
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}

                          <p className="text-xs text-[#a1a1aa] mt-3 italic">{selectedResult.details}</p>
                        </CardContent>
                      </Card>
                    </>
                  ) : (
                    <Card className="bg-[#14161a] border-white/5 h-full">
                      <CardContent className="flex flex-col items-center justify-center py-20">
                        <BarChart3 className="w-12 h-12 text-[#a1a1aa]/20 mb-4" />
                        <p className="text-sm text-[#a1a1aa]">Select an item to view visual scoring results</p>
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <Card className="bg-[#14161a] border-white/5 h-full">
              <CardContent className="flex flex-col items-center justify-center py-20">
                <Target className="w-12 h-12 text-[#a1a1aa]/20 mb-4" />
                <p className="text-sm text-[#a1a1aa] mb-2">Upload old chart predictions to score against actual outcomes</p>
                <p className="text-xs text-[#a1a1aa]/60 max-w-md text-center">
                  The system extracts predicted wave targets from your uploaded charts, fetches historical price data,
                  and generates visual overlays showing where predictions hit or missed.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}





